import { Coins, Trash2, Gift, Wrench, Undo2, FileText } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { salvageFull, salvageShort, tenantLocale, tenantTz } from "../../lib/company";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" }) : "—";

const METHOD_META = {
  sold:     { label: "Sold",     icon: Coins,  cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  scrapped: { label: "Scrapped", icon: Trash2, cls: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" },
  donated:  { label: "Donated",  icon: Gift,   cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200" },
  kept:     { label: "Kept",     icon: Wrench, cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200" },
};

/**
 * Single row in the NTS Impound table.
 * Wraps all the per-row presentation (debt vs recovered colours, status pill,
 * action buttons) so the parent can stay focused on state + data fetching.
 *
 * Callbacks lifted to the parent (`onClick` / `onDispose` / `onUndo` /
 * `onPaperwork`) so we don't re-create them per row.
 */
export default function NtsRow({ job, isAdmin, canUndoSignover, onClick, onDispose, onUndo, onUndoSignover, onPaperwork }) {
  const debt = job.released_to_nts_amount_owed || 0;
  const sale = job.nts_sold_amount;
  const rowNet = sale != null ? sale - debt : null;
  const disposed = !!job.nts_disposed_at;
  const meta = disposed ? METHOD_META[job.nts_disposal_method] : null;
  const Icon = meta?.icon;
  const paperworkCount = job.nts_paperwork?.length || 0;

  return (
    <tr
      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
      onClick={onClick}
      data-testid={`nts-row-${job.id}`}
    >
      <td className="px-3 py-2">
        <div className="font-semibold text-slate-900 dark:text-slate-100">{job.vehicle || "—"}</div>
        {job.rego && <div className="text-xs font-mono text-slate-500">{job.rego}</div>}
      </td>
      <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{job.customer_name || "—"}</td>
      <td className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap text-xs">
        {fmtDate(job.released_to_nts_at)}
      </td>
      <td className="px-3 py-2 text-left font-mono tabular-nums text-rose-700 dark:text-rose-300">
        {fmtMoney(debt)}
      </td>
      <td className="px-3 py-2">
        {disposed && meta ? (
          <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase", meta.cls)}>
            {Icon && <Icon className="h-3 w-3" />}
            {meta.label}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            In Inventory
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-slate-700 dark:text-slate-300 text-xs max-w-[260px]">
        {disposed ? (
          <>
            {job.nts_sold_to && <div className="font-semibold">{job.nts_sold_to}</div>}
            {job.nts_disposal_notes && (
              <div className="text-slate-500 truncate" title={job.nts_disposal_notes}>{job.nts_disposal_notes}</div>
            )}
            {job.nts_disposed_by && (
              <div className="text-[10px] text-slate-400">
                by {job.nts_disposed_by} · {fmtDate(job.nts_disposed_at)}
              </div>
            )}
          </>
        ) : (
          <span className="text-slate-300 dark:text-slate-600">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-700 dark:text-emerald-300">
        {sale != null ? fmtMoney(sale) : <span className="text-slate-300 dark:text-slate-600">—</span>}
      </td>
      <td className={cn(
        "px-3 py-2 text-right font-mono tabular-nums font-semibold",
        rowNet == null ? "text-slate-300 dark:text-slate-600"
          : rowNet >= 0 ? "text-blue-700 dark:text-blue-300"
          : "text-amber-700 dark:text-amber-300",
      )}>
        {rowNet != null ? `${rowNet >= 0 ? "+" : ""}${fmtMoney(rowNet)}` : "—"}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPaperwork(job)}
          className="text-xs mr-1 relative"
          title="View/upload signed sign-over paperwork"
          data-testid={`nts-paperwork-${job.id}`}
        >
          <FileText className="h-3 w-3" />
          Paperwork
          {paperworkCount > 0 && (
            <span
              className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-amber-600 rounded-full"
              data-testid={`nts-paperwork-badge-${job.id}`}
            >
              {paperworkCount}
            </span>
          )}
        </Button>
        {!disposed && (
          <Button
            size="sm"
            onClick={() => onDispose(job)}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white text-xs"
            data-testid={`nts-mark-disposed-${job.id}`}
          >
            Mark Disposed
          </Button>
        )}
        {canUndoSignover && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onUndoSignover(job)}
            className="text-xs ml-1 text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-950/40"
            data-testid={`nts-undo-signover-${job.id}`}
            title={`Reverse the sign-over — puts the vehicle back in the yard, off ${salvageShort()} inventory`}
          >
            <Undo2 className="h-3 w-3" /> Undo sign-over
          </Button>
        )}
        {disposed && isAdmin && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onUndo(job)}
            className="text-xs"
            data-testid={`nts-undo-disposed-${job.id}`}
            title={`Admin only — reverts to ${salvageShort()} inventory`}
          >
            <Undo2 className="h-3 w-3" /> Undo
          </Button>
        )}
      </td>
    </tr>
  );
}
